import { FormEvent, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Check, ChevronDown } from 'lucide-react';
import {
  fetchInfo,
  getCachedInfo,
  fetchReleasesSlim,
  parseBioContent,
  submitContact,
  type SlimRelease,
} from '@/lib/api';
import { portraitSurfaceStyle } from '@/lib/portraitEffects';
import {
  DEFAULT_PORTRAIT,
  isVideoPortrait,
  resolvePortrait,
} from '@/lib/portrait';
import { InfoSkeleton } from '@/components/ui/skeleton';
import { ContactTurnstile } from '@/components/list/ContactTurnstile';
import type { SiteInfo } from '@/types';

function bioFromCache() {
  const cached = getCachedInfo();
  return cached ? parseBioContent(cached.bio || '') : null;
}

export function InfoPanel({ onLoaded }: { onLoaded?: () => void }) {
  const initial = bioFromCache();
  const cachedInfo = getCachedInfo();
  const [paragraphs, setParagraphs] = useState<string[]>(() => initial?.paragraphs ?? []);
  const [bioHtml, setBioHtml] = useState<string | null>(() => initial?.bioHtml ?? null);
  const [siteInfo, setSiteInfo] = useState<SiteInfo | null>(() => cachedInfo);
  const [portraitSrc, setPortraitSrc] = useState<string | null>(() =>
    cachedInfo ? resolvePortrait(cachedInfo.portraitUrl) : null,
  );
  const [loading, setLoading] = useState(() => !initial);
  const [inquiryType, setInquiryType] = useState<'general' | 'release'>('general');
  const [releases, setReleases] = useState<SlimRelease[]>([]);
  const [sent, setSent] = useState(false);
  const [fading, setFading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [releaseSelect, setReleaseSelect] = useState('');
  const [message, setMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileRequired, setTurnstileRequired] = useState(true);
  const [honeypot, setHoneypot] = useState('');
  const year = new Date().getFullYear();

  const portraitStyle = useMemo(
    () => (siteInfo ? portraitSurfaceStyle(siteInfo) : {}),
    [siteInfo],
  );

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedInfo();
    if (cached) {
      const parsed = parseBioContent(cached.bio || '');
      setBioHtml(parsed.bioHtml);
      setParagraphs(parsed.paragraphs);
      setSiteInfo(cached);
      setPortraitSrc(resolvePortrait(cached.portraitUrl));
      setLoading(false);
      onLoaded?.();
      return;
    }
    setLoading(true);
    fetchInfo()
      .then((info) => {
        if (cancelled) return;
        const parsed = parseBioContent(info.bio || '');
        setBioHtml(parsed.bioHtml);
        setParagraphs(parsed.paragraphs);
        setSiteInfo(info);
        setPortraitSrc(resolvePortrait(info.portraitUrl));
        setLoading(false);
        onLoaded?.();
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setPortraitSrc(DEFAULT_PORTRAIT);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onLoaded]);

  useEffect(() => {
    if (inquiryType === 'release') {
      fetchReleasesSlim()
        .then((list) =>
          setReleases(
            [...list].sort(
              (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime(),
            ),
          ),
        )
        .catch(console.error);
    }
  }, [inquiryType]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (honeypot.trim()) return;
    if (turnstileRequired && !turnstileToken) {
      alert('Please complete the verification');
      return;
    }
    try {
      await submitContact({
        name,
        email,
        inquiryType,
        subject,
        releaseSelect,
        message,
        turnstileToken,
        website: honeypot,
      });
      setFading(true);
      window.setTimeout(() => setSent(true), 1000);
    } catch {
      alert('Error sending email');
    }
  };

  if (loading) {
    return (
      <div className="info-panel">
        {/* Same toolbar shell as Discography / Photography for shared mobile metrics */}
        <div className="catalog-toolbar">
          <div className="section-label">About</div>
        </div>
        <InfoSkeleton />
      </div>
    );
  }

  const portrait = portraitSrc || DEFAULT_PORTRAIT;

  return (
    <div className="info-panel">
      <div className="catalog-toolbar">
        <div className="section-label">About</div>
      </div>
      <div className="info-portrait" aria-hidden>
        <div className="portrait-layer" style={portraitStyle}>
          {isVideoPortrait(portrait) ? (
            <video
              className="portrait-still"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src={portrait} />
            </video>
          ) : (
            <img src={portrait} alt="" className="portrait-still" />
          )}
        </div>
      </div>
      {bioHtml ? (
        <div className="bio" dangerouslySetInnerHTML={{ __html: bioHtml }} />
      ) : (
        <div className="bio">
          {paragraphs.map((p, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
          ))}
        </div>
      )}
      <div className="contact-heading">
        Get in touch
        <ArrowUpRight size={20} aria-hidden />
      </div>
      {sent ? (
        <p className="sent-message">
          <Check size={18} aria-hidden />
          Gray has many plans for {year}, including reading your email.
        </p>
      ) : (
        <form
          className="contact-form"
          onSubmit={onSubmit}
          style={{
            transition: 'opacity 1s',
            opacity: fading ? 0 : 1,
          }}
        >
          <div className="form-pair">
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Name"
              aria-label="Name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email"
              aria-label="Email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="form-pair">
            <div className="select-field">
              <select
                id="inquiry-type"
                name="inquiry-type"
                aria-label="Inquiry type"
                required
                value={inquiryType}
                onChange={(e) => setInquiryType(e.target.value as 'general' | 'release')}
              >
                <option value="general">General Inquiry</option>
                <option value="release">Release Inquiry</option>
              </select>
              <ChevronDown
                className="select-chevron"
                size={16}
                aria-hidden
                strokeWidth={2}
              />
            </div>
            {inquiryType === 'general' ? (
              <input
                type="text"
                id="subject"
                name="subject"
                placeholder="Subject"
                aria-label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            ) : (
              <div className="select-field">
                <select
                  id="release-select"
                  name="release-select"
                  aria-label="Select a release"
                  required
                  value={releaseSelect}
                  onChange={(e) => setReleaseSelect(e.target.value)}
                >
                  <option value="" disabled>
                    Select a release
                  </option>
                  {releases.map((r) => (
                    <option key={r.id} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="select-chevron"
                  size={16}
                  aria-hidden
                  strokeWidth={2}
                />
              </div>
            )}
          </div>
          <textarea
            id="message"
            name="message"
            rows={5}
            placeholder="Message"
            aria-label="Message"
            required
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden
            className="contact-honeypot"
          />
          <div className="contact-actions">
            <ContactTurnstile
              onToken={setTurnstileToken}
              onExpire={() => setTurnstileToken('')}
              onSkip={() => {
                setTurnstileRequired(false);
                setTurnstileToken('');
              }}
            />
            <button type="submit">Send message</button>
          </div>
        </form>
      )}
    </div>
  );
}
